import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Users } from "lucide-react";

const AdminAuthors = () => {
  const [pubItems, setPubItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [pub, all] = await Promise.all([
          api.get("/admin/stats/authors", { params: { status: "published" } }),
          api.get("/admin/stats/authors", { params: { status: "all" } }),
        ]);
        setPubItems(pub.data.items || []);
        setAllItems(all.data.items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const totalPub = pubItems.reduce((s, i) => s + i.count, 0);
  const totalAll = allItems.reduce((s, i) => s + i.count, 0);

  // Merge into single table: published count + total count per author
  const merged = (() => {
    const m = new Map();
    for (const a of allItems) m.set(a.author, { author: a.author, all: a.count, published: 0 });
    for (const a of pubItems) {
      const cur = m.get(a.author) || { author: a.author, all: 0, published: 0 };
      cur.published = a.count;
      m.set(a.author, cur);
    }
    return Array.from(m.values()).sort((a, b) => b.published - a.published || b.all - a.all);
  })();

  return (
    <AdminShell title="Autori">
      <div className="max-w-3xl">
        <p className="font-serif italic text-base sm:text-lg text-[#4a4843] mb-8 sm:mb-10 leading-relaxed">
          Ogni post ha un autore (può essere lasciato vuoto). Qui vedi quanti post sono attribuiti a ciascun autore — separando quelli già pubblicati dal totale (incluse bozze e da controllare).
        </p>

        {loading ? (
          <p className="font-sans text-sm text-[#4a4843]">caricamento…</p>
        ) : merged.length === 0 ? (
          <p className="font-serif italic text-[#4a4843] py-12 text-center border border-dashed border-[#e5e1d8]">
            Nessun post nel database.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-8">
              <div className="border border-[#e5e1d8] bg-[#f9f8f6] p-5">
                <div className="flex items-center gap-2 mb-2 text-[#c5a873]">
                  <Users size={14} />
                  <span className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#4a4843]">Autori</span>
                </div>
                <p className="font-serif text-3xl text-[#1a1918]">{merged.filter((m) => m.author !== "(senza autore)").length}</p>
              </div>
              <div className="border border-[#e5e1d8] bg-[#f9f8f6] p-5">
                <span className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#4a4843]">Post pubblicati</span>
                <p className="font-serif text-3xl text-[#1a1918] mt-2">{totalPub}</p>
                <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#4a4843]/70 mt-1">su {totalAll} totali</p>
              </div>
            </div>

            <ul className="divide-y divide-[#e5e1d8] border-t border-b border-[#e5e1d8]">
              <li className="flex items-center gap-3 py-3 font-sans text-[10px] tracking-[0.3em] uppercase text-[#4a4843]">
                <span className="flex-1">Autore</span>
                <span className="w-24 text-right">Pubblicati</span>
                <span className="w-24 text-right">Totale</span>
              </li>
              {merged.map((m) => (
                <li
                  key={m.author}
                  data-testid={`author-row-${m.author}`}
                  className="flex items-center gap-3 py-3"
                >
                  <span className={`flex-1 font-serif text-lg ${m.author === "(senza autore)" ? "italic text-[#4a4843]" : "text-[#1a1918]"}`}>
                    {m.author}
                  </span>
                  <span className="w-24 text-right font-serif text-lg text-[#1a1918]">
                    {m.published}
                  </span>
                  <span className="w-24 text-right font-serif text-lg text-[#4a4843]">
                    {m.all}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </AdminShell>
  );
};

export default AdminAuthors;
